<template>
  <div class="mobile-page">
    <div class="sub-nav">
      <button class="back-btn" @click="back"><el-icon :size="18"><ArrowLeft /></el-icon></button>
      <span class="nav-title">巡检安排</span>
      <span class="nav-right"><button class="refresh-btn" @click="load"><el-icon :size="16"><Refresh /></el-icon></button></span>
    </div>

    <div class="mobile-body">
      <div class="search-line">
        <el-input v-model="kw" placeholder="搜索日期 / 班次 / 负责人 / 成员" clearable size="default">
          <template #prefix><el-icon><Search /></el-icon></template>
        </el-input>
        <button class="sort-btn" @click="asc = !asc">{{ asc ? '升序' : '降序' }}</button>
      </div>

      <div v-if="loading" class="center-box">
        <el-icon class="is-loading" :size="28"><Loading /></el-icon>
        <p class="sub-text">加载中…</p>
      </div>
      <div v-else-if="error" class="white-card center-box">
        <el-icon :size="36" color="#c5221f"><WarningFilled /></el-icon>
        <p class="sub-text">{{ error }}</p>
        <el-button size="small" type="primary" plain style="margin-top:10px" @click="load">重试</el-button>
      </div>
      <div v-else-if="groups.length === 0" class="white-card center-box">
        <el-icon :size="40" color="#c0c4cc"><Timer /></el-icon>
        <p class="sub-text">{{ filtered.length ? '没有匹配的安排' : '暂无巡检安排（请先发布配置）' }}</p>
      </div>

      <template v-else>
        <div class="day-block" v-for="g in groups" :key="g.date">
          <div class="day-head">
            <span class="day-date">{{ formatDate(g.date) }}</span>
            <span v-if="g.date === today" class="pill pill-green">今天</span>
            <span v-else-if="g.date < today" class="pill pill-gray">已过</span>
          </div>
          <div class="duty-card" v-for="d in g.items" :key="d.id">
            <div class="dc-top">
              <span class="dc-title">{{ d.title || '巡检安排' }}</span>
              <span class="pill pill-blue">{{ d.shift || '白班' }}</span>
            </div>
            <div class="dc-people">
              <div class="dc-row"><span class="dc-k">负责人</span><span class="dc-v strong">{{ d.leader || '—' }}</span></div>
              <div class="dc-row"><span class="dc-k">成员</span>
                <span class="dc-v">
                  <span v-if="members(d.members).length" class="member-chips">
                    <span v-for="(m, i) in members(d.members)" :key="i" class="member-chip">{{ m }}</span>
                  </span>
                  <span v-else class="muted">—</span>
                </span>
              </div>
              <div class="dc-row" v-if="d.phone"><span class="dc-k">电话</span><a class="dc-v phone" :href="'tel:' + d.phone">{{ d.phone }}</a></div>
              <div class="dc-row" v-if="d.remark"><span class="dc-k">备注</span><span class="dc-v">{{ d.remark }}</span></div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { fetchSchedule } from '../api'
import dayjs from 'dayjs'

const router = useRouter()
function back() { router.back() }

const loading = ref(true)
const error = ref('')
const kw = ref('')
const asc = ref(true)
const duty = ref([])

const today = dayjs().format('YYYY-MM-DD')

function members(str) {
  return String(str || '').split(/[,，、;；\s/]+/).map(s => s.trim()).filter(Boolean)
}

const filtered = computed(() => {
  const k = kw.value.trim().toLowerCase()
  if (!k) return duty.value
  return duty.value.filter(d => {
    const hay = [d.plan_date, d.shift, d.title, d.leader, d.members, d.phone, d.remark].join(' ').toLowerCase()
    return hay.includes(k)
  })
})

const groups = computed(() => {
  const sorted = [...filtered.value].sort((a, b) => {
    const c = String(a.plan_date || '').localeCompare(String(b.plan_date || ''))
    if (c !== 0) return asc.value ? c : -c
    const s = String(a.shift || '').localeCompare(String(b.shift || ''))
    return asc.value ? s : -s
  })
  const map = new Map()
  for (const d of sorted) {
    const key = d.plan_date || '未知日期'
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(d)
  }
  return Array.from(map, ([date, items]) => ({ date, items }))
})

function formatDate(date) {
  if (!date) return '未知日期'
  const d = dayjs(date)
  const base = d.format('YYYY年MM月DD日')
  const w = '日一二三四五六'.charAt(d.day())
  return `${base} 周${w}`
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const r = await fetchSchedule()
    duty.value = r.duty || []
  } catch (e) {
    error.value = e.message || '加载失败'
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.search-line { display: flex; gap: 8px; margin-bottom: 10px; }
.search-line .el-input { flex: 1; }
.sort-btn {
  flex-shrink: 0; border: 1px solid #e4e7ed; background: #fff; color: #5f6368;
  border-radius: 10px; padding: 0 14px; font-size: 13px; cursor: pointer;
}

.day-block { margin-bottom: 14px; }
.day-head { display: flex; align-items: center; gap: 8px; margin: 4px 2px 8px; }
.day-date { font-size: 14px; font-weight: 700; color: #3c4043; }

.duty-card {
  background: #fff; border-radius: 12px; padding: 12px 14px; margin-bottom: 8px;
  box-shadow: 0 2px 10px rgba(31,45,61,.05);
}
.dc-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.dc-title { font-size: 14.5px; font-weight: 700; color: #202124; }
.dc-people { display: flex; flex-direction: column; gap: 5px; }
.dc-row { display: flex; gap: 8px; font-size: 13px; color: #3c4043; line-height: 1.5; }
.dc-k { width: 52px; flex-shrink: 0; color: #9aa0a6; }
.dc-v { word-break: break-all; }
.dc-v.strong { font-weight: 600; color: #1a73e8; }
.muted { color: #b0b3bb; }
.member-chips { display: flex; flex-wrap: wrap; gap: 4px; }
.member-chip {
  background: #f1f3f4; color: #3c4043; border-radius: 10px; padding: 1px 9px; font-size: 12px;
}
.phone { color: #1a73e8; text-decoration: none; }
</style>
